import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { FaLeaf } from "react-icons/fa";
import axios from "axios";
import { TestimonialsSkeleton } from "../../../../components/general/skeleton/SkeletonBlock";
import "../../../../assets/css/customer/home/testimonials/Testimonials.css";
import MyContext from "../../../../context/MyContext";

// Inline SVG placeholder — koi file server par nahi honi chahiye is liye
// yeh kabhi 404/loop nahi karega (jab customer ne apni image upload na ki ho)
const PLACEHOLDER_CUSTOMER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'>" +
      "<rect width='80' height='80' fill='#e8f5f0'/>" +
      "<circle cx='40' cy='32' r='14' fill='#0ac46c'/>" +
      "<rect x='16' y='52' width='48' height='22' rx='11' fill='#0ac46c'/>" +
      "</svg>",
  );

const Testimonials = () => {
  const { baseUrl, baseUrlForFiles } = useContext(MyContext);

  const [testimonialsData, setTestimonialsData] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  // ── Backend se top-rated (4+) reviews fetch karna, jinme review
  //     text bhi ho — system khud select karta hai, koi admin
  //     curation nahi chahiye ──
  const fetchTestimonials = () => {
    axios
      .get(`${baseUrl}/customer/review/viewTestimonials`)
      .then((response) => {
        if (response.data.success == true) {
          setTestimonialsData(response.data.testimonials || []);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const getCustomerImage = (image) =>
    image ? `${baseUrlForFiles}/${image}` : PLACEHOLDER_CUSTOMER;

  const handleImgError = (e) => {
    e.currentTarget.onerror = null; // infinite retry loop band
    e.currentTarget.src = PLACEHOLDER_CUSTOMER;
  };

  // ── "bought X from Y" — jo bhi data available ho usi se banta hai ──
  const getRoleText = (t) => {
    if (t.productName && t.shopName) {
      return `Bought ${t.productName} from ${t.shopName}`;
    }
    if (t.shopName) {
      return `Customer at ${t.shopName}`;
    }
    if (t.productName) {
      return `Bought ${t.productName}`;
    }
    return "Verified Customer";
  };

  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % testimonialsData.length);

  const prevSlide = () =>
    setCurrentSlide(
      (prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length,
    );

  const goToSlide = (index) => setCurrentSlide(index);

  const getPrevIndex = () =>
    currentSlide === 0 ? testimonialsData.length - 1 : currentSlide - 1;

  const getNextIndex = () =>
    currentSlide === testimonialsData.length - 1 ? 0 : currentSlide + 1;

  // Auto-play every 5 seconds
  useEffect(() => {
    if (testimonialsData.length < 2) return;
    const autoPlayInterval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(autoPlayInterval);
  }, [testimonialsData.length]);

  if (loading) return <TestimonialsSkeleton />;
  if (testimonialsData.length === 0) return null;

  const currentTestimonial = testimonialsData[currentSlide];

  return (
    <section className="testimonials-section">
      <Container>
        <Row className="align-items-center">
          {/* Left Side: Image Carousel Slider */}
          <Col xs={12} lg={6} className="mb-5 mb-lg-0">
            <div className="testimonials-images-slider">
              {/* Background Image - Previous */}
              <div className="slider-image slider-bg-left">
                <img
                  src={getCustomerImage(testimonialsData[getPrevIndex()].image)}
                  alt={testimonialsData[getPrevIndex()].author}
                  onError={handleImgError}
                />
              </div>

              {/* Active/Front Image - Current */}
              <div className="slider-image slider-active">
                <img
                  src={getCustomerImage(currentTestimonial.image)}
                  alt={currentTestimonial.author}
                  onError={handleImgError}
                />
              </div>

              {/* Background Image - Next */}
              <div className="slider-image slider-bg-right">
                <img
                  src={getCustomerImage(testimonialsData[getNextIndex()]?.image)}
                  alt={testimonialsData[getNextIndex()]?.author}
                  onError={handleImgError}
                />
              </div>

              {/* Floating Leaves */}
              <div className="floating-leaves">
                <FaLeaf className="leaf leaf-1" />
                <FaLeaf className="leaf leaf-2" />
              </div>
            </div>
          </Col>

          {/* Right Side: Testimonial Content */}
          <Col xs={12} lg={6}>
            <div className="testimonials-content">
              {/* Badge */}
              <div className="content-badge">
                <FaLeaf /> OUR TESTIMONIALS
              </div>

              {/* Heading */}
              <h2>
                Hear What Our
                <br />
                <span className="text-highlight-green">Global Clients</span> Say
              </h2>

              <div className="testimonial-slider">
                {/* Rating Box */}
                <div className="rating-box-container">
                  <div className="rating-box">
                    <h3>{Number(currentTestimonial.rating).toFixed(1)}</h3>
                    <div className="stars">★★★★★</div>
                    <p>Trusted by 500+ companies</p>
                  </div>
                </div>

                {/* Testimonial Quote */}
                <blockquote className="testimonial-quote">
                  "{currentTestimonial.quote}"
                </blockquote>

                {/* Author Info */}
                <div className="testimonial-author">
                  <img
                    src={getCustomerImage(currentTestimonial.image)}
                    alt={currentTestimonial.author}
                    onError={handleImgError}
                  />
                  <div>
                    <h4>{currentTestimonial.author}</h4>
                    <p>{getRoleText(currentTestimonial)}</p>
                  </div>
                </div>

                {/* Slider Controls */}
                <div className="slider-controls">
                  <div className="slider-dots">
                    {testimonialsData.map((_, index) => (
                      <span
                        key={index}
                        className={`dot ${currentSlide === index ? "active" : ""}`}
                        onClick={() => goToSlide(index)}
                        role="button"
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Testimonials;
